﻿import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { startOfMonth, endOfMonth, subMonths, format, parseISO } from 'date-fns';
import {
  Users, CalendarCheck, TreePalm, Package, TrendingDown, TrendingUp,
  Download, UserCheck, UserX, Clock, AlarmClock,
  Target, Trophy, Activity, TriangleAlert, Gauge,
} from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { StatCard } from '@/components/ui/stat-card';
import { StatTile } from '@/components/ui/stat-tile';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PermissionGate } from '@/components/guards/PermissionGate';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { usePermission } from '@/hooks/usePermission';
import {
  reportsService,
  type ReportsFilter,
  type AttendanceEmployeeItem,
  type TaskEmployeeStatus,
  type GoalReportStatus,
  type GoalEmployeeScoreItem,
  type LeaveEmployeeItem,
} from '@/services/reports.service';
import { departmentsService } from '@/services/departments.service';
import { usersService, type StaffListItem } from '@/services/users.service';
import { assetsService, type AssetListItem } from '@/services/assets.service';
import { financeService, type PurchaseListItem } from '@/services/finance.service';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { CHART_TOOLTIP_STYLE } from '@/lib/chartTheme';
import { formatDate } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';

/** Delay before a typed search reaches the server, matching the goals tabs. */
const SEARCH_DEBOUNCE_MS = 350;


// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMinutes(mins: number): string {
  if (!mins || mins <= 0) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Leave days are fractional (half-day = 0.5) — show 0.5, not 0.50 or 1. */
function fmtDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function fmtCurrency(n: number): string {
  if (n >= 10_00_000) return `₹${(n / 10_00_000).toFixed(1)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
}

// ── SVG Sparkline ─────────────────────────────────────────────────────────────

interface SparklineProps {
  data: number[];
  color?: string;
  height?: number;
  className?: string;
}

function Sparkline({ data, color = '#6366f1', height = 48, className }: SparklineProps) {
  if (!data.length) return <div className={cn('flex items-center justify-center text-xs text-muted-foreground', className)} style={{ height }}>No data</div>;
  // single point produces a degenerate zero-width path — duplicate it so it renders as a flat horizontal line
  const plotData = data.length === 1 ? [data[0]!, data[0]!] : data;
  const max = Math.max(...plotData, 1);
  const min = Math.min(...plotData, 0);
  const range = max - min || 1;
  const w = 300;
  const h = height;
  const pad = 4;
  const pts = plotData.map((v, i) => {
    const x = pad + (i / Math.max(plotData.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  const area = `M${pts[0]} ` + pts.slice(1).map((p) => `L${p}`).join(' ') + ` L${pts[pts.length - 1]!.split(',')[0]},${h} L${pts[0]!.split(',')[0]},${h} Z`;
  const line = `M${pts[0]} ` + pts.slice(1).map((p) => `L${p}`).join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={cn('w-full', className)} style={{ height }} preserveAspectRatio="none">
      <path d={area} fill={color} fillOpacity={0.12} />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Progress Bar Row ──────────────────────────────────────────────────────────

function BarRow({ label, count, max, color = '#6366f1' }: { label: string; count: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="truncate text-foreground">{label}</span>
        <span className="ml-2 shrink-0 text-xs text-muted-foreground">{count}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

// ── Color Dot List ────────────────────────────────────────────────────────────

function DotList({ items }: { items: { label: string; count: number; color?: string | null }[] }) {
  if (!items.length) return <p className="text-sm text-muted-foreground py-4 text-center">No data</p>;
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color ?? '#94a3b8' }} />
            <span className="text-sm truncate">{item.label}</span>
          </div>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground shrink-0">
            {item.count}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type Tab = 'attendance' | 'leave' | 'assets' | 'purchase' | 'staff' | 'task' | 'goal';

/** Tabs everyone with reports.view gets. The Goal tab is added per-permission. */
const BASE_TABS: { key: Tab; label: string }[] = [
  { key: 'attendance', label: 'Attendance' },
  { key: 'leave', label: 'Leave' },
  { key: 'assets', label: 'Assets' },
  { key: 'purchase', label: 'Purchase' },
  { key: 'staff', label: 'Staff' },
  { key: 'task', label: 'Task' },
];

// ── Attendance Tab ────────────────────────────────────────────────────────────

function AttendanceTab({ filter }: { filter: ReportsFilter }) {
  const [attPage, setAttPage] = useState(1);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // setState sits in the timeout callback, not the effect body — the shape
  // react-hooks/set-state-in-effect allows.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setAttPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const summaryQ = useQuery({
    queryKey: ['reports', 'attendance', 'range-summary', filter],
    queryFn: () => reportsService.getAttendanceRangeSummary(filter),
  });
  const dailyQ = useQuery({
    queryKey: ['reports', 'attendance', 'daily-trend', filter],
    queryFn: () => reportsService.getAttendanceDailyTrend(filter),
  });
  const deptQ = useQuery({
    queryKey: ['reports', 'attendance', 'dept-trend', filter],
    queryFn: () => reportsService.getAttendanceDeptTrend(filter),
  });
  const empListQ = useQuery({
    queryKey: ['reports', 'attendance', 'employees', filter, debouncedQ, attPage],
    queryFn: () => reportsService.getAttendanceEmployeeList(filter, attPage, debouncedQ),
  });

  const summary = summaryQ.data;
  const daily = dailyQ.data ?? [];
  const deptTrend = deptQ.data ?? [];
  const maxDept = Math.max(1, ...deptTrend.map((d) => d.count));

  const empCols: Column<AttendanceEmployeeItem>[] = [
    {
      key: 'name',
      header: 'Employee',
      cell: (r) => (
        <div>
          <Link
            to={`/hrms/staff/${r.user_id}?tab=attendance`}
            className="font-medium text-sm text-primary hover:underline"
          >
            {r.name}
          </Link>
          <p className="text-xs text-muted-foreground">{r.employee_code}</p>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      cell: (r) => r.department_name
        ? <span className="text-sm font-medium">{r.department_name}</span>
        : <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">No dept.</span>,
    },
    {
      key: 'days_present',
      header: 'Present',
      className: 'text-center',
      cell: (r) => <span className="font-semibold text-emerald-600">{fmtDays(r.days_present)}</span>,
    },
    {
      key: 'days_late',
      header: 'Late',
      className: 'text-center',
      cell: (r) => (
        <span className={r.days_late > 0 ? 'font-semibold text-amber-500' : 'text-muted-foreground'}>
          {r.days_late}
        </span>
      ),
    },
    {
      key: 'days_absent',
      header: 'Absent',
      className: 'text-center',
      cell: (r) => (
        <span className={r.days_absent > 0 ? 'font-semibold text-rose-500' : 'text-muted-foreground'}>
          {r.days_absent}
        </span>
      ),
    },
    {
      key: 'total_hours',
      header: 'Total Hours',
      cell: (r) => <span className="font-mono text-sm">{fmtMinutes(r.total_minutes)}</span>,
    },
    {
      key: 'avg_hours',
      header: 'Avg / Day',
      cell: (r) => {
        const avg = r.days_present > 0 ? Math.round(r.total_minutes / r.days_present) : 0;
        return <span className="font-mono text-sm text-muted-foreground">{fmtMinutes(avg)}</span>;
      },
    },
  ];

  const empPagination = empListQ.data
    ? {
        page: empListQ.data.pagination.page,
        totalPages: empListQ.data.pagination.totalPages,
        total: empListQ.data.pagination.total,
        onPageChange: (p: number) => setAttPage(p),
      }
    : undefined;

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total Present" value={summary?.present ?? '—'} icon={UserCheck} tone="emerald" />
        <StatTile label="Total Absent" value={summary?.absent ?? '—'} icon={UserX} tone="rose" />
        <StatTile label="Late Arrivals" value={summary?.late ?? '—'} icon={AlarmClock} tone="amber" />
        <StatTile label="Half Days" value={summary?.half_day ?? '—'} icon={Clock} tone="sky" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Daily Attendance Trend</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {daily.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data for this period</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradPresent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradLate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradAbsent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    tickFormatter={(d) => format(parseISO(d as string), 'd MMM')}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelFormatter={(d) => format(parseISO(d as string), 'd MMM yyyy')}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="present" name="Present" stroke="#10b981" strokeWidth={2} fill="url(#gradPresent)" dot={false} />
                  <Area type="monotone" dataKey="late"    name="Late"    stroke="#f59e0b" strokeWidth={2} fill="url(#gradLate)"    dot={false} />
                  <Area type="monotone" dataKey="absent"  name="Absent"  stroke="#f43f5e" strokeWidth={2} fill="url(#gradAbsent)"  dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Attendance by Department</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {deptTrend.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              deptTrend.map((d) => (
                <BarRow key={d.department} label={d.department} count={d.count} max={maxDept} color="#6366f1" />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Employees Table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">All Employees</h3>
          {empListQ.data && (
            <span className="text-xs text-muted-foreground">
              {empListQ.data.pagination.total} employees
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="Search employee name or code…"
            containerClassName="min-w-[220px] flex-1 max-w-sm"
          />
        </div>

        <DataTable
          columns={empCols}
          rows={empListQ.data?.data ?? []}
          rowKey={(r) => r.user_id}
          loading={empListQ.isLoading}
          error={empListQ.isError}
          onRetry={() => empListQ.refetch()}
          empty="No attendance data matches these filters."
          pagination={empPagination}
        />
      </div>
    </div>
  );
}

// ── Leave Tab ─────────────────────────────────────────────────────────────────

function LeaveTab({ filter }: { filter: ReportsFilter }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [departmentId, setDepartmentId] = useState('');

  // setState lives in the timeout callback, not the effect body — the shape
  // react-hooks/set-state-in-effect allows.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  // This tab's own Department is authoritative: it drives the charts and the
  // table together so they can never show different departments. The page-level
  // dropdown deliberately does not reach this tab.
  const tabFilter: ReportsFilter = {
    date_from: filter.date_from,
    date_to: filter.date_to,
    department_id: departmentId || undefined,
  };

  const deptsQ = useQuery({
    queryKey: ['departments', 'list-all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const dailyQ = useQuery({
    queryKey: ['reports', 'leave', 'daily-trend', tabFilter],
    queryFn: () => reportsService.getLeaveDailyTrend(tabFilter),
  });
  const typeDistQ = useQuery({
    queryKey: ['reports', 'leave', 'type-dist', tabFilter],
    queryFn: () => reportsService.getLeaveTypeDistribution(tabFilter),
  });
  const listQ = useQuery({
    queryKey: ['reports', 'leave', 'employees', tabFilter, debouncedQ, page],
    queryFn: () => reportsService.getLeaveEmployeeList(tabFilter, page, debouncedQ),
  });

  const daily = dailyQ.data ?? [];
  const typeDist = typeDistQ.data ?? [];
  const maxType = Math.max(1, ...typeDist.map((t) => t.count));

  const leaveListCols: Column<LeaveEmployeeItem>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (r) => (
        <div>
          <Link
            to={`/hrms/staff/${r.user_id}?tab=leaves`}
            className="font-medium text-sm text-primary hover:underline"
          >
            {r.name || '—'}
          </Link>
          <p className="text-xs text-muted-foreground">{r.employee_code ?? ''}</p>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      cell: (r) => r.department_name
        ? <span className="text-sm font-medium">{r.department_name}</span>
        : <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">No dept.</span>,
    },
    {
      key: 'request_count',
      header: 'Requests',
      className: 'text-center',
      cell: (r) => <span className="font-semibold">{r.request_count}</span>,
    },
    {
      key: 'total_days',
      header: 'Total Days',
      className: 'text-center',
      cell: (r) => <span className="font-semibold text-sky-700">{fmtDays(r.total_days)}</span>,
    },
    {
      key: 'lop_days',
      header: 'LOP Days',
      className: 'text-center',
      cell: (r) => (
        <span className={r.lop_days > 0 ? 'font-semibold text-rose-500' : 'text-muted-foreground'}>
          {fmtDays(r.lop_days)}
        </span>
      ),
    },
    {
      key: 'short',
      header: 'Short Leaves',
      className: 'text-center',
      cell: (r) => r.short_count > 0
        ? (
          <div>
            <p className="font-semibold text-amber-600">{r.short_count}</p>
            <p className="text-xs text-muted-foreground">{fmtMinutes(r.short_minutes)}</p>
          </div>
        )
        : <span className="text-muted-foreground">0</span>,
    },
    {
      key: 'last_leave_date',
      header: 'Last Leave',
      cell: (r) => r.last_leave_date
        ? <span className="text-muted-foreground text-sm">{formatDate(r.last_leave_date)}</span>
        : <span className="text-muted-foreground">—</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Daily Leave Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyQ.isLoading ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">Loading…</div>
            ) : daily.length === 0 ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">No approved leaves in this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradLeave" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0f766e" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#0f766e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    tickFormatter={(d) => format(parseISO(d as string), 'd MMM')}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(v) => [v ?? 0, 'Leaves'] as [number, string]}
                    labelFormatter={(d) => format(parseISO(d as string), 'd MMM yyyy')}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Leaves"
                    stroke="#0f766e"
                    strokeWidth={2}
                    fill="url(#gradLeave)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Leave Type Distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {typeDist.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              typeDist.map((t) => (
                <BarRow
                  key={t.type_name}
                  label={t.type_name}
                  count={t.count}
                  max={maxType}
                  color={t.color ?? '#f59e0b'}
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Leave by Employee</h3>
          {listQ.data && (
            <span className="text-xs text-muted-foreground">{listQ.data.pagination.total} employees</span>
          )}
        </div>

        {/* Department here drives the charts above as well as this table. */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="Search employee name or code…"
            containerClassName="min-w-[220px] flex-1 max-w-sm"
          />

          <Select
            value={departmentId || '__all__'}
            onValueChange={(v) => { setDepartmentId(v === '__all__' ? '' : v); setPage(1); }}
          >
            <SelectTrigger className="h-9 w-44">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Departments</SelectItem>
              {(deptsQ.data?.data ?? []).map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DataTable
          columns={leaveListCols}
          rows={listQ.data?.data ?? []}
          rowKey={(r) => r.user_id}
          loading={listQ.isLoading}
          error={listQ.isError}
          onRetry={() => listQ.refetch()}
          empty="No approved leave matches these filters."
          pagination={
            listQ.data
              ? {
                  page: listQ.data.pagination.page,
                  totalPages: listQ.data.pagination.totalPages,
                  total: listQ.data.pagination.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}

// ── Assets Tab ────────────────────────────────────────────────────────────────

// Assets carry no date, so this tab takes no page-level filter — its own bar is
// the only thing that narrows the table.
function AssetsTab() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [userId, setUserId] = useState('');

  // setState here is inside the timeout callback, not the effect body, so this
  // is the shape react-hooks/set-state-in-effect permits.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q]);

  const updateFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v === '__all__' ? '' : v);
    setPage(1);
  };

  const statusDistQ = useQuery({
    queryKey: ['reports', 'assets', 'status-dist'],
    queryFn: () => reportsService.getAssetStatusDistribution(),
  });
  const deptDistQ = useQuery({
    queryKey: ['reports', 'assets', 'dept-dist'],
    queryFn: () => reportsService.getAssetDeptDistribution(),
  });
  const deptsQ = useQuery({
    queryKey: ['departments', 'list-all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });
  // /users/picker is unpaginated (LIMIT 500). The paginated /users list caps at
  // 100, which would silently drop staff from the dropdown.
  const usersQ = useQuery({
    queryKey: ['users', 'picker'],
    queryFn: () => usersService.picker(),
  });

  // Blank must become undefined — the backend rejects '' as a non-uuid.
  const assetParams = {
    page,
    limit: 20,
    q: debouncedQ || undefined,
    department_id: departmentId || undefined,
    assigned_to_user_id: userId || undefined,
  };
  const assetsQ = useQuery({
    queryKey: ['reports', 'assets', 'list', assetParams],
    queryFn: () => assetsService.listAssets(assetParams),
  });

  const statusDist = statusDistQ.data ?? [];
  const deptDist = deptDistQ.data ?? [];
  const maxDept = Math.max(1, ...deptDist.map((d) => d.count));

  const assetCols: Column<AssetListItem>[] = [
    {
      key: 'asset_code',
      header: 'Asset Code',
      cell: (r) => <span className="font-mono text-xs font-medium">{r.asset_code}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      cell: (r) => r.category?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'department',
      header: 'Department',
      cell: (r) => r.department?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'assigned_to',
      header: 'Assignee',
      cell: (r) => r.assigned_to?.name ?? <span className="text-muted-foreground">Unassigned</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) =>
        r.status ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: r.status.color }} />
            <span className="text-sm">{r.status.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'created_at',
      header: 'Date',
      cell: (r) => <span className="text-muted-foreground">{formatDate(r.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Asset Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DotList
              items={statusDist.map((s) => ({ label: s.status_name, count: s.count, color: s.color }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Asset by Department</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {deptDist.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              deptDist.map((d) => (
                <BarRow key={d.department} label={d.department} count={d.count} max={maxDept} color="#8b5cf6" />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">All Assets</h3>
          {assetsQ.data && (
            <span className="text-xs text-muted-foreground">
              {assetsQ.data.pagination.total} {assetsQ.data.pagination.total === 1 ? 'asset' : 'assets'}
            </span>
          )}
        </div>

        {/* Filter bar. One search box spans asset code, model, category,
            assignee and department (see buildAssetFilters on the backend). */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="Search code, category, assignee, department…"
            containerClassName="min-w-[240px] flex-1 max-w-sm"
          />

          <Select value={departmentId || '__all__'} onValueChange={updateFilter(setDepartmentId)}>
            <SelectTrigger className="h-9 w-44">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Departments</SelectItem>
              {(deptsQ.data?.data ?? []).map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={userId || '__all__'} onValueChange={updateFilter(setUserId)}>
            <SelectTrigger className="h-9 w-44">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Users</SelectItem>
              {(usersQ.data ?? []).map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {[u.first_name, u.last_name].filter(Boolean).join(' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DataTable
          columns={assetCols}
          rows={assetsQ.data?.data ?? []}
          rowKey={(r) => r.id}
          loading={assetsQ.isLoading}
          error={assetsQ.isError}
          onRetry={() => assetsQ.refetch()}
          empty="No assets match these filters."
          pagination={
            assetsQ.data
              ? {
                  page: assetsQ.data.pagination.page,
                  totalPages: assetsQ.data.pagination.totalPages,
                  total: assetsQ.data.pagination.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}

// ── Purchase Tab ──────────────────────────────────────────────────────────────

function PurchaseTab({ filter }: { filter: ReportsFilter }) {
  const [page, setPage] = useState(1);

  const dailyQ = useQuery({
    queryKey: ['reports', 'purchase', 'daily-trend', filter],
    queryFn: () => reportsService.getPurchaseDailyTrend(filter),
  });
  const catQ = useQuery({
    queryKey: ['reports', 'purchase', 'category', filter],
    queryFn: () => reportsService.getPurchaseCategoryBreakdown(filter),
  });
  const purchasesQ = useQuery({
    queryKey: ['reports', 'purchase', 'list', filter, page],
    queryFn: () => financeService.listPurchases({
      from: filter.date_from,
      to: filter.date_to,
      page,
      limit: 20,
    }),
  });

  const daily = dailyQ.data ?? [];
  const catData = catQ.data ?? [];
  const maxCat = Math.max(1, ...catData.map((c) => c.total));
  const debitSeries = daily.map((d) => d.debit);
  const creditSeries = daily.map((d) => d.credit);

  const purchaseCols: Column<PurchaseListItem>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      cell: (r) => <span className="font-mono text-xs">{r.invoice_number}</span>,
    },
    {
      key: 'vendor',
      header: 'Vendor',
      cell: (r) => r.vendor_name,
    },
    {
      key: 'category',
      header: 'Category',
      cell: (r) => r.category_name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'total',
      header: 'Total',
      cell: (r) => <span className="font-medium">₹{parseFloat(r.total_amount).toLocaleString()}</span>,
    },
    {
      key: 'paid',
      header: 'Paid',
      cell: (r) => <span className="text-emerald-600">₹{parseFloat(r.paid_amount).toLocaleString()}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <span className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          r.status === 'Paid' ? 'bg-emerald-50 text-emerald-700' :
          r.status === 'Partially Paid' ? 'bg-amber-50 text-amber-700' :
          'bg-rose-50 text-rose-700',
        )}>
          {r.status}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      cell: (r) => <span className="text-muted-foreground">{formatDate(r.purchase_date)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Purchase vs Payment Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No purchases in this period</p>
            ) : (
              <>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-400 inline-block" />Debit (Total)</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" />Credit (Paid)</span>
                </div>
                <Sparkline data={debitSeries} color="#f43f5e" height={36} />
                <Sparkline data={creditSeries} color="#10b981" height={36} />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Category Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {catData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              catData.map((c) => (
                <BarRow
                  key={c.category}
                  label={c.category}
                  count={Math.round(c.total)}
                  max={maxCat}
                  color="#f43f5e"
                />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Purchase Ledger</h3>
        <DataTable
          columns={purchaseCols}
          rows={purchasesQ.data?.data ?? []}
          rowKey={(r) => r.id}
          loading={purchasesQ.isLoading}
          error={purchasesQ.isError}
          onRetry={() => purchasesQ.refetch()}
          empty="No purchases in this period."
          pagination={
            purchasesQ.data
              ? {
                  page: purchasesQ.data.pagination.page,
                  totalPages: purchasesQ.data.pagination.totalPages,
                  total: purchasesQ.data.pagination.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}

// ── Staff Tab ─────────────────────────────────────────────────────────────────

function StaffTab({ filter }: { filter: ReportsFilter }) {
  const [page, setPage] = useState(1);

  const deptDistQ = useQuery({
    queryKey: ['reports', 'staff', 'dept-dist', filter],
    queryFn: () => reportsService.getStaffDeptDistribution(filter),
  });
  const staffQ = useQuery({
    queryKey: ['reports', 'staff', 'list', filter, page],
    queryFn: () => usersService.list({
      department_id: filter.department_id,
      is_active: true,
      page,
      limit: 20,
    }),
  });

  const deptDist = deptDistQ.data ?? [];
  const maxDept = Math.max(1, ...deptDist.map((d) => d.count));

  const staffCols: Column<StaffListItem>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (r) => (
        <div>
          <p className="font-medium text-sm">{r.first_name} {r.last_name}</p>
          <p className="text-xs text-muted-foreground">{r.employee_code}</p>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      cell: (r) => r.department_name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'designation',
      header: 'Designation',
      cell: (r) => r.designation_name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'roles',
      header: 'Role',
      cell: (r) => (
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {r.roles?.[0] ?? '—'}
        </span>
      ),
    },
    {
      key: 'joining_date',
      header: 'Joining Date',
      cell: (r) => r.joining_date ? <span className="text-muted-foreground">{formatDate(r.joining_date)}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <span className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          r.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700',
        )}>
          {r.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Staff by Department</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {deptDist.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            ) : (
              deptDist.map((d) => (
                <BarRow key={d.department} label={d.department} count={d.count} max={maxDept} color="#0ea5e9" />
              ))
            )}
          </CardContent>
        </Card>

      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Staff Directory</h3>
        <DataTable
          columns={staffCols}
          rows={staffQ.data?.data ?? []}
          rowKey={(r) => r.id}
          loading={staffQ.isLoading}
          error={staffQ.isError}
          onRetry={() => staffQ.refetch()}
          empty="No active staff found."
          pagination={
            staffQ.data
              ? {
                  page: staffQ.data.pagination.page,
                  totalPages: staffQ.data.pagination.totalPages,
                  total: staffQ.data.pagination.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}

// ── Task Tab ──────────────────────────────────────────────────────────────────

const TASK_STATUS_COLORS: Record<string, string> = {
  pending_accept: '#f59e0b',
  in_progress:    '#0ea5e9',
  completed:      '#10b981',
  discarded:      '#6b7280',
};

const TASK_STATUS_LABELS: Record<string, string> = {
  pending_accept: 'Pending Accept',
  in_progress:    'In Progress',
  completed:      'Completed',
  discarded:      'Discarded',
};

function TaskTab({ filter }: { filter: ReportsFilter }) {
  const completionQ = useQuery({
    queryKey: ['reports', 'tasks', 'completion-trend', filter],
    queryFn: () => reportsService.getTaskCompletionTrend(filter),
  });
  const deptStatusQ = useQuery({
    queryKey: ['reports', 'tasks', 'dept-status', filter],
    queryFn: () => reportsService.getTaskStatusByDept(filter),
  });
  const empStatusQ = useQuery({
    queryKey: ['reports', 'tasks', 'emp-status', filter],
    queryFn: () => reportsService.getTaskStatusByEmployee(filter),
  });

  const completion = completionQ.data ?? [];
  const deptStatus = deptStatusQ.data ?? [];
  const empStatus = empStatusQ.data ?? [];

  const empCols: Column<TaskEmployeeStatus>[] = [
    {
      key: 'employee_name',
      header: 'Employee Name',
      cell: (r) => (
        <div>
          <span className="font-medium">{r.employee_name}</span>
          {r.employee_code && <span className="ml-1.5 text-xs text-muted-foreground">({r.employee_code})</span>}
        </div>
      ),
    },
    { key: 'department_name', header: 'Department', cell: (r) => r.department_name },
    { key: 'pending_accept', header: 'Pending Accept', cell: (r) => <span className="text-amber-600">{r.pending_accept}</span> },
    { key: 'in_progress', header: 'In Progress', cell: (r) => <span className="text-teal-600">{r.in_progress}</span> },
    { key: 'completed', header: 'Completed', cell: (r) => <span className="text-emerald-600 font-medium">{r.completed}</span> },
    { key: 'discarded', header: 'Discarded', cell: (r) => <span className="text-rose-400">{r.discarded}</span> },
    {
      key: 'total',
      header: 'Total',
      cell: (r) => {
        const t = r.pending_accept + r.in_progress + r.completed + r.discarded;
        return <span className="font-semibold">{t}</span>;
      },
    },
  ];

  const trendData = completion.map((d) => ({
    ...d,
    label: (() => { try { return format(parseISO(d.date), 'd MMM'); } catch { return d.date; } })(),
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Line chart — 30-day completion trend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">30-Day Completion Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {completionQ.isLoading ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">Loading…</div>
            ) : completion.length === 0 ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">No completed tasks in this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    interval="preserveStartEnd"
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={false}
                    width={32}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(v) => [v ?? 0, 'Completed'] as [number, string]}
                    labelFormatter={(l) => l}
                  />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#10b981' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Stacked bar chart — task status by department */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Task Status by Department</CardTitle>
          </CardHeader>
          <CardContent>
            {deptStatusQ.isLoading ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">Loading…</div>
            ) : deptStatus.length === 0 ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">No data for this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={deptStatus} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="department"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    angle={-15}
                    textAnchor="end"
                    interval={0}
                    height={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(v, name) => [v ?? 0, TASK_STATUS_LABELS[name as string] ?? name] as [number, string]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                    formatter={(name) => TASK_STATUS_LABELS[name] ?? name}
                  />
                  {Object.keys(TASK_STATUS_COLORS).map((key) => (
                    <Bar key={key} dataKey={key} stackId="a" fill={TASK_STATUS_COLORS[key]} maxBarSize={48} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={empCols}
        rows={empStatus}
        rowKey={(r) => r.employee_name + r.department_name}
        loading={empStatusQ.isLoading}
        error={empStatusQ.isError}
        onRetry={() => empStatusQ.refetch()}
        empty="No task data for this period."
      />
    </div>
  );
}

// ── Goal Tab ──────────────────────────────────────────────────────────────────

// Declared locally rather than imported from @/components/goals — feature
// components stay inside their own module (see GoalAssigneeSelect for the same
// call). Only the labels and hexes are duplicated, not behaviour.
const GOAL_STATUS_LABELS: Record<GoalReportStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  submitted: 'Submitted',
  achieved: 'Achieved',
  missed: 'Missed',
  cancelled: 'Cancelled',
};

const GOAL_STATUS_COLORS: Record<GoalReportStatus, string> = {
  draft: '#94a3b8',
  active: '#0d9488',
  submitted: '#6366f1',
  achieved: '#10b981',
  missed: '#f43f5e',
  cancelled: '#cbd5e1',
};

const GOAL_DEPT_SERIES: { key: 'achieved' | 'active' | 'missed' | 'overdue'; label: string; color: string }[] = [
  { key: 'achieved', label: 'Achieved', color: '#10b981' },
  { key: 'active', label: 'Active', color: '#0d9488' },
  { key: 'missed', label: 'Missed', color: '#f43f5e' },
  { key: 'overdue', label: 'Overdue', color: '#f59e0b' },
];

/** Compact inline progress bar for the scorecard table. */
function ProgressCell({ value }: { value: number }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: pct >= 75 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#f43f5e' }}
        />
      </div>
      <span className="text-xs font-medium tabular-nums w-10 text-right">{pct}%</span>
    </div>
  );
}

/**
 * Wraps a chart in the four required states. Every existing tab open-codes this
 * inconsistently — AttendanceTab has no loading branch at all.
 */
function ChartState({
  loading, error, empty, onRetry, children,
}: {
  loading: boolean; error: boolean; empty: boolean;
  onRetry: () => void; children: ReactNode;
}) {
  if (loading) {
    return <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">Loading…</div>;
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[240px] gap-2">
        <p className="text-sm text-muted-foreground">Could not load this chart.</p>
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      </div>
    );
  }
  if (empty) {
    return <p className="text-sm text-muted-foreground text-center py-16">No goal data for this period</p>;
  }
  return <>{children}</>;
}

function GoalTab({ filter }: { filter: ReportsFilter }) {
  // Pagination resets when the filter changes because the page remounts it via a
  // key — see the call site. The other tabs keep a stale page and can strand the
  // user on an empty one.
  const [page, setPage] = useState(1);

  const summaryQ = useQuery({
    queryKey: ['reports', 'goals', 'summary', filter],
    queryFn: () => reportsService.getGoalSummary(filter),
  });
  const statusQ = useQuery({
    queryKey: ['reports', 'goals', 'status-distribution', filter],
    queryFn: () => reportsService.getGoalStatusDistribution(filter),
  });
  const deptQ = useQuery({
    queryKey: ['reports', 'goals', 'dept-breakdown', filter],
    queryFn: () => reportsService.getGoalDeptBreakdown(filter),
  });
  const catQ = useQuery({
    queryKey: ['reports', 'goals', 'category-breakdown', filter],
    queryFn: () => reportsService.getGoalCategoryBreakdown(filter),
  });
  const trendQ = useQuery({
    queryKey: ['reports', 'goals', 'completion-trend', filter],
    queryFn: () => reportsService.getGoalCompletionTrend(filter),
  });
  const scoreQ = useQuery({
    queryKey: ['reports', 'goals', 'employees', filter, page],
    queryFn: () => reportsService.getGoalEmployeeScorecard(filter, page),
  });

  const summary = summaryQ.data;
  const statusDist = statusQ.data ?? [];
  const deptRows = deptQ.data ?? [];
  const catRows = catQ.data ?? [];
  const trend = trendQ.data ?? [];
  const maxCat = Math.max(1, ...catRows.map((c) => c.total));

  const scoreCols: Column<GoalEmployeeScoreItem>[] = [
    {
      key: 'name',
      header: 'Employee',
      cell: (r) => (
        <div>
          <Link to={`/hrms/staff/${r.user_id}`} className="font-medium text-sm text-primary hover:underline">
            {r.name}
          </Link>
          <p className="text-xs text-muted-foreground">{r.employee_code ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      cell: (r) => r.department_name
        ? <span className="text-sm">{r.department_name}</span>
        : <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">No dept.</span>,
    },
    { key: 'total', header: 'Goals', className: 'text-center', cell: (r) => <span className="font-semibold">{r.total}</span> },
    { key: 'achieved', header: 'Achieved', className: 'text-center', cell: (r) => <span className="font-semibold text-emerald-600">{r.achieved}</span> },
    { key: 'active', header: 'Active', className: 'text-center', cell: (r) => <span className="text-muted-foreground">{r.active}</span> },
    {
      key: 'overdue',
      header: 'Overdue',
      className: 'text-center',
      cell: (r) => <span className={r.overdue > 0 ? 'font-semibold text-rose-500' : 'text-muted-foreground'}>{r.overdue}</span>,
    },
    {
      key: 'at_risk',
      header: 'At Risk',
      className: 'text-center',
      cell: (r) => <span className={r.at_risk > 0 ? 'font-semibold text-amber-500' : 'text-muted-foreground'}>{r.at_risk}</span>,
    },
    { key: 'avg_progress', header: 'Avg Progress', cell: (r) => <ProgressCell value={r.avg_progress} /> },
    {
      key: 'avg_rating',
      header: 'Avg Rating',
      className: 'text-center',
      cell: (r) => r.avg_rating > 0
        ? <span className="font-medium tabular-nums">{r.avg_rating.toFixed(1)} / 5</span>
        : <span className="text-muted-foreground">—</span>,
    },
  ];

  return (
    <div className="space-y-4">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatTile label="Total Goals" value={summary?.total ?? '—'} icon={Target} tone="sky" />
        <StatTile label="Achieved" value={summary?.achieved ?? '—'} icon={Trophy} tone="emerald" />
        <StatTile label="Active" value={summary?.active ?? '—'} icon={Activity} tone="teal" />
        <StatTile label="Overdue" value={summary?.overdue ?? '—'} icon={AlarmClock} tone="rose" />
        <StatTile label="At Risk" value={summary?.at_risk ?? '—'} icon={TriangleAlert} tone="amber" />
        <StatTile
          label="Avg Progress"
          value={summary ? `${summary.avg_progress}%` : '—'}
          sub={summary ? `${summary.achievement_rate}% achievement rate` : undefined}
          icon={Gauge}
          tone="violet"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Goals by Status</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ChartState
              loading={statusQ.isLoading}
              error={statusQ.isError}
              empty={statusDist.every((s) => s.count === 0)}
              onRetry={() => statusQ.refetch()}
            >
              <DotList
                items={statusDist.map((s) => ({
                  label: GOAL_STATUS_LABELS[s.status],
                  count: s.count,
                  color: GOAL_STATUS_COLORS[s.status],
                }))}
              />
            </ChartState>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Goals by Department</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ChartState
              loading={deptQ.isLoading}
              error={deptQ.isError}
              empty={deptRows.length === 0}
              onRetry={() => deptQ.refetch()}
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={deptRows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
                  <XAxis
                    dataKey="department"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    angle={-15}
                    textAnchor="end"
                    interval={0}
                    height={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={32}
                    allowDecimals={false}
                  />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  {GOAL_DEPT_SERIES.map((s) => (
                    <Bar key={s.key} dataKey={s.key} name={s.label} stackId="a" fill={s.color} maxBarSize={48} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartState>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Goals by Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <ChartState
              loading={catQ.isLoading}
              error={catQ.isError}
              empty={catRows.length === 0}
              onRetry={() => catQ.refetch()}
            >
              <>
                {catRows.map((c) => (
                  <BarRow key={c.category} label={c.category} count={c.total} max={maxCat} color="#6366f1" />
                ))}
              </>
            </ChartState>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Goals Closed per Week</CardTitle>
            {/* Different population from the rest of the tab — see the service comment. */}
            <p className="text-xs text-muted-foreground">By close date, not due date</p>
          </CardHeader>
          <CardContent className="pt-0">
            <ChartState
              loading={trendQ.isLoading}
              error={trendQ.isError}
              empty={trend.length === 0}
              onRetry={() => trendQ.refetch()}
            >
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    tickFormatter={(d) => format(parseISO(d as string), 'd MMM')}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelFormatter={(d) => `Week of ${format(parseISO(d as string), 'd MMM yyyy')}`}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="achieved" name="Achieved" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="missed" name="Missed" stroke="#f43f5e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartState>
          </CardContent>
        </Card>
      </div>

      {/* Employee scorecard */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Employee Scorecard</h3>
          {scoreQ.data && (
            <span className="text-xs text-muted-foreground">{scoreQ.data.pagination.total} employees</span>
          )}
        </div>
        <DataTable
          columns={scoreCols}
          rows={scoreQ.data?.data ?? []}
          rowKey={(r) => r.user_id}
          loading={scoreQ.isLoading}
          error={scoreQ.isError}
          onRetry={() => scoreQ.refetch()}
          empty="No goals due in this period."
          pagination={
            scoreQ.data
              ? {
                  page: scoreQ.data.pagination.page,
                  totalPages: scoreQ.data.pagination.totalPages,
                  total: scoreQ.data.pagination.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ReportsOverviewPage() {
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(() => format(startOfMonth(today), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(endOfMonth(today), 'yyyy-MM-dd'));
  const [departmentId, setDepartmentId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<Tab>('attendance');
  const perms = usePermission();

  const tabs = useMemo(
    () => (perms.has(PERMISSIONS.REPORTS_GOALS_VIEW)
      ? [...BASE_TABS, { key: 'goal' as Tab, label: 'Goal' }]
      : BASE_TABS),
    [perms],
  );

  const filter: ReportsFilter = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    department_id: departmentId || undefined,
  };

  // Remounting a tab on filter change is how its pagination gets reset — the
  // useEffect+setState alternative trips react-hooks/set-state-in-effect.
  const filterKey = `${dateFrom}|${dateTo}|${departmentId}`;

  const kpisQ = useQuery({
    queryKey: ['reports', 'kpis', filter],
    queryFn: () => reportsService.getKpis(filter),
  });

  const deptsQ = useQuery({
    queryKey: ['departments', 'list-all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const kpis = kpisQ.data;
  const depts = deptsQ.data?.data ?? [];

  function applyThisMonth() {
    setDateFrom(format(startOfMonth(today), 'yyyy-MM-dd'));
    setDateTo(format(endOfMonth(today), 'yyyy-MM-dd'));
  }

  function applyLastMonth() {
    const last = subMonths(today, 1);
    setDateFrom(format(startOfMonth(last), 'yyyy-MM-dd'));
    setDateTo(format(endOfMonth(last), 'yyyy-MM-dd'));
  }

  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    // Only the Goal tab has a real export so far; the rest still advertise it.
    if (activeTab !== 'goal') {
      toast.info(`Export for "${activeTab}" tab — coming soon`);
      return;
    }
    setExporting(true);
    try {
      await reportsService.exportGoalCsv(filter);
      toast.success('Goal report downloaded');
    } catch {
      toast.error('Could not export the goal report. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <PermissionGate permission={PERMISSIONS.REPORTS_VIEW}>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Reports Overview</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Cross-module analytics: attendance, leave, assets, finance, staff, tasks, and goals</p>
          </div>
          <PermissionGate permission={PERMISSIONS.REPORTS_EXPORT}>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="shrink-0">
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </PermissionGate>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={departmentId || '__all__'} onValueChange={(v) => setDepartmentId(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-10 w-48">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Departments</SelectItem>
              {depts.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DateRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
            className="w-56"
          />

          <Button variant="outline" size="sm" onClick={applyThisMonth}>
            This Month
          </Button>
          <Button variant="outline" size="sm" onClick={applyLastMonth}>
            Last Month
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard
            label="Total Staff"
            value={kpis?.total_staff ?? '—'}
            icon={Users}
            tone="sky"
          />
          <StatCard
            label="Attendance Rate"
            value={kpis ? `${kpis.attendance_rate}%` : '—'}
            icon={CalendarCheck}
            tone="emerald"
          />
          <StatCard
            label="Leaves Approved"
            value={kpis?.leaves_approved ?? '—'}
            icon={TreePalm}
            tone="amber"
          />
          <StatCard
            label="Active Assets"
            value={kpis?.active_assets ?? '—'}
            icon={Package}
            tone="violet"
          />
          <StatCard
            label="Purchase Debit"
            value={kpis ? fmtCurrency(kpis.purchase_debit) : '—'}
            icon={TrendingDown}
            tone="rose"
          />
          <StatCard
            label="Purchase Credit"
            value={kpis ? fmtCurrency(kpis.purchase_credit) : '—'}
            icon={TrendingUp}
            tone="teal"
          />
        </div>

        {/* Tab switcher */}
        <Tabs
          tabs={tabs}
          active={activeTab}
          onChange={(key) => setActiveTab(key as Tab)}
        />

        {/* Tab content */}
        <div>
          {activeTab === 'attendance' && <AttendanceTab filter={filter} />}
          {/* Keyed on dates only — this tab owns its own Department, so a
              page-level department change must not wipe that choice. */}
          {activeTab === 'leave' && <LeaveTab key={`${dateFrom}|${dateTo}`} filter={filter} />}
          {activeTab === 'assets' && <AssetsTab />}
          {activeTab === 'purchase' && <PurchaseTab filter={filter} />}
          {activeTab === 'staff' && <StaffTab filter={filter} />}
          {activeTab === 'task' && <TaskTab filter={filter} />}
          {activeTab === 'goal' && <GoalTab key={filterKey} filter={filter} />}
        </div>
      </div>
    </PermissionGate>
  );
}
